/**
 * Individual rows for the timeline — three tiers per person:
 *   1. Utilisation row  (short, read-only)
 *   2. One booking-bar row per existing booking (click to edit, drag edges to resize)
 *   3. "New booking" row  (drag to create — suppressed for archived individuals)
 */
import { useState } from "react";
import clsx from "clsx";

import type { BookingModalState } from "./booking-modal";

// ─── types ────────────────────────────────────────────────────────────────────

export type Column = {
    key: string; // YYYY-MM for monthly, "YYYY-Qn" for quarterly, "YYYY" for yearly
    label: string;
    months: string[]; // YYYY-MM strings that fall in this column
    monthWeights: Record<string, number>;
};

export type IndividualData = {
    id: string;
    name: string;
    teamName: string;
    departmentName: string;
    archiveDate: string | null; // YYYY-MM-DD, null means active
    bookings: {
        id: string;
        projectId: string;
        projectName: string;
        serviceId: string;
        startDate: string; // YYYY-MM-DD
        endDate: string; // YYYY-MM-DD
        fte: number; // base flat allocation value (0–1)
        allocationUnit: "fte_pct" | "fte_fraction";
        manDaysPerManMonth: number;
        allocations: { month: string; fte: number }[];
    }[];
    utilisation: {
        month: string;
        workingDays: number;
        grossCapacityMd: number;
        effectiveCapacityFte: number;
        bookedFte: number;
        utilisationPercent: number;
        flag: "over" | "under" | "ok";
    }[];
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// UTC-safe: parse YYYY-MM-DD to UTC milliseconds
function dateToMs(dateStr: string): number {
    const [y, m, d] = dateStr.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
}

// UTC-safe: UTC milliseconds to YYYY-MM-DD
function msToDateString(ms: number): string {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

// Returns the first day of a YYYY-MM month as YYYY-MM-DD
function firstDayOf(ym: string): string {
    return `${ym}-01`;
}

// Returns the last day of a YYYY-MM month as YYYY-MM-DD
function lastDayOf(ym: string): string {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m, 0).getDate();
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Returns the YYYY-MM of the first month in a column
function colStartMonth(col: Column): string {
    return col.months[0];
}

// Returns the YYYY-MM of the last month in a column
function colEndMonth(col: Column): string {
    return col.months[col.months.length - 1];
}

// Returns the fractional position [0,1] of a UTC ms value within a column.
// 0 = start of column's first day; 1 = exclusive end of column's last day.
function colFraction(col: Column, ms: number): number {
    const firstMs = dateToMs(firstDayOf(col.months[0]));
    const lastMs = dateToMs(lastDayOf(col.months[col.months.length - 1])) + 86400000;
    return Math.max(0, Math.min(1, (ms - firstMs) / (lastMs - firstMs)));
}

// Given a mouse event on a column cell, returns the calendar date at that position.
function mousePositionToDate(e: React.MouseEvent<HTMLElement>, col: Column): string {
    const rect = e.currentTarget.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(0.9999, (e.clientX - rect.left) / rect.width));
    const firstMs = dateToMs(firstDayOf(col.months[0]));
    const lastMs = dateToMs(lastDayOf(col.months[col.months.length - 1])) + 86400000;
    const targetMs = firstMs + fraction * (lastMs - firstMs);
    // Floor to start of UTC day
    const dayMs = Math.floor(targetMs / 86400000) * 86400000;
    const colFirstDay = firstDayOf(col.months[0]);
    const colLastDay = lastDayOf(col.months[col.months.length - 1]);
    const result = msToDateString(dayMs);
    return result < colFirstDay ? colFirstDay : result > colLastDay ? colLastDay : result;
}

export function columnFte(allocations: { month: string; fte: number }[], col: Column): number | null {
    const totalWeight = Object.values(col.monthWeights).reduce((s, w) => s + w, 0);
    if (totalWeight === 0) return null;
    let weightedSum = 0;
    for (const a of allocations) {
        const m = a.month.slice(0, 7);
        if (!(m in col.monthWeights)) continue;
        weightedSum += a.fte * col.monthWeights[m];
    }
    return weightedSum / totalWeight;
}

export function columnUtilisation(
    utilisation: IndividualData["utilisation"],
    col: Column,
): { percent: number; bookedMd: number; capacityMd: number; flag: "over" | "under" | "ok" } | null {
    const byMonth = Object.fromEntries(utilisation.map((u) => [u.month.slice(0, 7), u]));
    let totalBookedMd = 0;
    let totalCapacityMd = 0;
    let numMonths = 0;
    for (const m of col.months) {
        const u = byMonth[m];
        if (!u) continue;
        numMonths++;
        totalBookedMd += u.bookedFte * u.effectiveCapacityFte * u.workingDays;
        totalCapacityMd += u.effectiveCapacityFte * u.workingDays;
    }
    if (numMonths === 0) return null;
    const percent = totalCapacityMd > 0 ? (totalBookedMd / totalCapacityMd) * 100 : 0;
    return {
        percent,
        bookedMd: totalBookedMd,
        capacityMd: totalCapacityMd,
        flag: percent > 110 ? "over" : percent < 80 ? "under" : "ok",
    };
}

// ─── individual rows component ────────────────────────────────────────────────

type Props = {
    individual: IndividualData;
    columns: Column[];
    projectColorMap: Record<string, string>;
    projectFilter?: string;
    onOpenModal: (state: BookingModalState) => void;
};

export const IndividualRows = ({ individual, columns, projectColorMap, projectFilter, onOpenModal }: Props) => {
    const [collapsed, setCollapsed] = useState(false);
    const visibleBookings = projectFilter
        ? individual.bookings.filter((b) => b.projectId === projectFilter)
        : individual.bookings;
    const rowSpan = collapsed ? 1 : 1 + visibleBookings.length + (individual.archiveDate ? 0 : 1);

    return (
        <>
            {/* ── tier 1: name + utilisation ── */}
            <UtilisationRow
                individual={individual}
                columns={columns}
                rowSpan={rowSpan}
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed((c) => !c)}
            />

            {/* ── tier 2: one row per booking ── */}
            {!collapsed &&
                visibleBookings.map((booking) => (
                    <BookingRow
                        key={booking.id}
                        booking={booking}
                        individual={individual}
                        columns={columns}
                        projectColorMap={projectColorMap}
                        onOpenModal={onOpenModal}
                    />
                ))}

            {/* ── tier 3: new booking row (suppressed for archived or collapsed) ── */}
            {!collapsed && !individual.archiveDate && (
                <NewBookingRow individual={individual} columns={columns} onOpenModal={onOpenModal} />
            )}
        </>
    );
};

// ─── tier 1: utilisation row ──────────────────────────────────────────────────

const UtilisationRow = ({
    individual,
    columns,
    rowSpan,
    collapsed,
    onToggleCollapse,
}: {
    individual: IndividualData;
    columns: Column[];
    rowSpan: number;
    collapsed: boolean;
    onToggleCollapse: () => void;
}) => (
    <tr className="border-b">
        <td className="px-3 py-1 sticky left-0 bg-white align-top" rowSpan={rowSpan}>
            <div className="flex items-center gap-2">
                <button
                    onClick={onToggleCollapse}
                    className="text-slate-400 hover:text-slate-600 shrink-0 w-3 text-xs leading-none"
                    title={collapsed ? "Expand" : "Collapse"}
                >
                    {collapsed ? "▶" : "▼"}
                </button>
                <div>
                    <span
                        className={clsx("text-sm font-medium", individual.archiveDate && "line-through text-slate-400")}
                    >
                        {individual.name}
                    </span>
                    <div className="text-xs text-slate-400">{individual.departmentName}</div>
                    <AggregateUtilisationLabel individual={individual} columns={columns} />
                </div>
            </div>
        </td>
        <td className="px-3 py-1 text-xs text-slate-500 italic">Utilisation</td>
        {columns.map((col) => {
            const util = columnUtilisation(individual.utilisation, col);
            return (
                <td key={col.key} className="px-1 text-center w-24">
                    {util !== null ? (
                        <span
                            className={clsx("text-xs font-medium", {
                                "text-red-600": util.flag === "over",
                                "text-yellow-600": util.flag === "under",
                                "text-slate-400": util.flag === "ok",
                            })}
                        >
                            {util.percent.toFixed(0)}%{" "}
                            <span className="font-normal text-slate-400">
                                ({util.bookedMd.toFixed(1)}/{util.capacityMd.toFixed(1)} MDs)
                            </span>
                        </span>
                    ) : null}
                </td>
            );
        })}
    </tr>
);

// ─── tier 2: booking bar row ──────────────────────────────────────────────────

type BookingRowProps = {
    booking: IndividualData["bookings"][number];
    individual: IndividualData;
    columns: Column[];
    projectColorMap: Record<string, string>;
    onOpenModal: (state: BookingModalState) => void;
};

const BookingRow = ({ booking, individual, columns, projectColorMap, onOpenModal }: BookingRowProps) => {
    const [dragState, setDragState] = useState<{
        edge: "left" | "right";
        previewStart: string; // YYYY-MM-DD
        previewEnd: string; // YYYY-MM-DD
    } | null>(null);

    const activeStart = dragState ? dragState.previewStart : booking.startDate;
    const activeEnd = dragState ? dragState.previewEnd : booking.endDate;
    const color = projectColorMap[booking.projectId] ?? "bg-slate-200 text-slate-700";

    const openEditModal = (startDate: string, endDate: string) => {
        onOpenModal({
            mode: "edit",
            bookingId: booking.id,
            individualId: individual.id,
            individualName: individual.name,
            startDate,
            endDate,
            projectId: booking.projectId,
            serviceId: booking.serviceId,
            fte: booking.fte,
            allocationUnit: booking.allocationUnit,
        });
    };

    const handleEdgeMouseDown = (e: React.MouseEvent, edge: "left" | "right") => {
        e.stopPropagation();
        e.preventDefault();
        setDragState({ edge, previewStart: booking.startDate, previewEnd: booking.endDate });
    };

    const handleCellMouseMove = (e: React.MouseEvent<HTMLTableCellElement>, col: Column) => {
        if (!dragState) return;
        const date = mousePositionToDate(e, col);
        setDragState((prev) => {
            if (!prev) return prev;
            if (prev.edge === "left") {
                const newStart = date <= prev.previewEnd ? date : prev.previewEnd;
                if (newStart === prev.previewStart) return prev;
                return { ...prev, previewStart: newStart };
            } else {
                const newEnd = date >= prev.previewStart ? date : prev.previewStart;
                if (newEnd === prev.previewEnd) return prev;
                return { ...prev, previewEnd: newEnd };
            }
        });
    };

    const handleMouseUp = () => {
        if (!dragState) return;
        const { previewStart, previewEnd } = dragState;
        setDragState(null);
        openEditModal(previewStart, previewEnd);
    };

    // Find the key of the first visible column that overlaps the booking (for label placement)
    let firstVisibleColKey: string | null = null;
    for (const col of columns) {
        if (lastDayOf(colEndMonth(col)) >= activeStart && firstDayOf(colStartMonth(col)) <= activeEnd) {
            firstVisibleColKey = col.key;
            break;
        }
    }

    return (
        <tr
            className="border-b"
            onMouseUp={dragState ? handleMouseUp : undefined}
            onMouseLeave={dragState ? handleMouseUp : undefined}
        >
            <td className="px-3 py-1 text-xs text-slate-400">
                <span className="truncate max-w-36 block">{booking.projectName}</span>
            </td>

            {columns.map((col) => {
                const colFirstDay = firstDayOf(colStartMonth(col));
                const colLastDay = lastDayOf(colEndMonth(col));

                const overlaps = colLastDay >= activeStart && colFirstDay <= activeEnd;
                // isFirst: booking starts within this column (left edge handle lives here)
                const isFirst = overlaps && activeStart >= colFirstDay && activeStart <= colLastDay;
                // isLast: booking ends within this column (right edge handle lives here)
                const isLast = overlaps && activeEnd >= colFirstDay && activeEnd <= colLastDay;

                // Fractional insets so the bar starts/ends at the exact date position
                const leftInset = isFirst ? colFraction(col, dateToMs(activeStart)) : 0;
                const rightInset = isLast ? 1 - colFraction(col, dateToMs(activeEnd) + 86400000) : 0;

                return (
                    <td key={col.key} className="p-0 w-24 select-none" onMouseMove={(e) => handleCellMouseMove(e, col)}>
                        {overlaps ? (
                            <div
                                className={clsx(
                                    "relative h-6 flex items-center cursor-pointer",
                                    color,
                                    isFirst && "rounded-l",
                                    isLast && "rounded-r",
                                    dragState && "opacity-80",
                                )}
                                style={{
                                    marginLeft: `${leftInset * 100}%`,
                                    marginRight: `${rightInset * 100}%`,
                                }}
                                onClick={() => !dragState && openEditModal(booking.startDate, booking.endDate)}
                            >
                                {/* left resize handle */}
                                {isFirst && (
                                    <span
                                        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/10 rounded-l"
                                        onMouseDown={(e) => handleEdgeMouseDown(e, "left")}
                                    />
                                )}
                                {/* label on the leftmost visible column */}
                                {col.key === firstVisibleColKey && (
                                    <span className="text-xs px-2 truncate pointer-events-none">
                                        {booking.allocationUnit === "fte_fraction"
                                            ? `${booking.fte.toFixed(1)} FTE`
                                            : `${(booking.fte * 100).toFixed(0)}%`}
                                    </span>
                                )}
                                {/* right resize handle */}
                                {isLast && (
                                    <span
                                        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/10 rounded-r"
                                        onMouseDown={(e) => handleEdgeMouseDown(e, "right")}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="h-6" />
                        )}
                    </td>
                );
            })}
        </tr>
    );
};

// ─── tier 3: new booking row ──────────────────────────────────────────────────

type NewBookingRowProps = {
    individual: IndividualData;
    columns: Column[];
    onOpenModal: (state: BookingModalState) => void;
};

const NewBookingRow = ({ individual, columns, onOpenModal }: NewBookingRowProps) => {
    const [dragStart, setDragStart] = useState<string | null>(null); // YYYY-MM-DD
    const [dragEnd, setDragEnd] = useState<string | null>(null); // YYYY-MM-DD
    const isDragging = dragStart !== null;

    const [previewStart, previewEnd] =
        dragStart && dragEnd ? ([dragStart, dragEnd].sort() as [string, string]) : [null, null];

    const handleMouseDown = (e: React.MouseEvent<HTMLTableCellElement>, col: Column) => {
        const date = mousePositionToDate(e, col);
        setDragStart(date);
        setDragEnd(date);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLTableCellElement>, col: Column) => {
        if (!isDragging) return;
        const date = mousePositionToDate(e, col);
        setDragEnd((prev) => (prev === date ? prev : date));
    };

    const handleMouseUp = () => {
        if (!isDragging || !previewStart || !previewEnd) {
            setDragStart(null);
            setDragEnd(null);
            return;
        }
        const startDate = previewStart;
        const endDate = previewEnd;
        setDragStart(null);
        setDragEnd(null);
        onOpenModal({
            mode: "create",
            individualId: individual.id,
            individualName: individual.name,
            startDate,
            endDate,
        });
    };

    return (
        <tr
            className="border-b hover:bg-slate-50/50"
            onMouseUp={isDragging ? handleMouseUp : undefined}
            onMouseLeave={isDragging ? handleMouseUp : undefined}
        >
            <td className="px-3 py-1 text-xs text-slate-300">+ new booking</td>
            {columns.map((col) => {
                const colFirstDay = firstDayOf(colStartMonth(col));
                const colLastDay = lastDayOf(colEndMonth(col));
                const inPreview =
                    previewStart !== null &&
                    previewEnd !== null &&
                    colLastDay >= previewStart &&
                    colFirstDay <= previewEnd;

                const isPreviewFirst = inPreview && previewStart >= colFirstDay && previewStart <= colLastDay;
                const isPreviewLast = inPreview && previewEnd >= colFirstDay && previewEnd <= colLastDay;
                const leftInset = isPreviewFirst ? colFraction(col, dateToMs(previewStart!)) : 0;
                const rightInset = isPreviewLast ? 1 - colFraction(col, dateToMs(previewEnd!) + 86400000) : 0;

                return (
                    <td
                        key={col.key}
                        className="p-0 w-24 select-none cursor-crosshair"
                        onMouseDown={(e) => handleMouseDown(e, col)}
                        onMouseMove={(e) => handleMouseMove(e, col)}
                    >
                        {inPreview ? (
                            <div
                                className="h-6 rounded bg-blue-200 border border-blue-400 border-dashed opacity-70"
                                style={{
                                    marginLeft: `${leftInset * 100}%`,
                                    marginRight: `${rightInset * 100}%`,
                                }}
                            />
                        ) : (
                            <div className="h-6" />
                        )}
                    </td>
                );
            })}
        </tr>
    );
};

// ─── aggregate utilisation label (for name cell) ──────────────────────────────

const AggregateUtilisationLabel = ({ individual, columns }: { individual: IndividualData; columns: Column[] }) => {
    const byMonth = Object.fromEntries(individual.utilisation.map((u) => [u.month.slice(0, 7), u]));
    let totalBookedMd = 0;
    let totalCapacityMd = 0;
    for (const col of columns) {
        for (const m of col.months) {
            const u = byMonth[m];
            if (!u) continue;
            totalBookedMd += u.bookedFte * u.effectiveCapacityFte * u.workingDays;
            totalCapacityMd += u.effectiveCapacityFte * u.workingDays;
        }
    }
    const percent = totalCapacityMd > 0 ? Math.round((totalBookedMd / totalCapacityMd) * 100) : 0;
    const flag: "over" | "under" | "ok" =
        totalCapacityMd === 0 ? "ok" : percent > 110 ? "over" : percent < 80 ? "under" : "ok";
    return (
        <span
            className={clsx("text-xs", {
                "text-red-600": flag === "over",
                "text-yellow-600": flag === "under",
                "text-slate-400": flag === "ok",
            })}
        >
            {percent}% ({totalBookedMd.toFixed(1)}/{totalCapacityMd.toFixed(1)} MDs)
        </span>
    );
};

// ─── re-export helpers used by timeline-page ─────────────────────────────────
export { colStartMonth };
